<template>
  <div class="skulist-page">
    <!-- Header -->
    <div class="sk-header">
      <div>
        <span class="header-eyebrow">Shopify</span>
        <h2 class="header-title">SKU List</h2>
      </div>
    </div>

    <!-- KPI Strip -->
    <div class="kpi-strip">
      <div class="kpi-card" v-for="item in dataResumen" :key="item.nombre">
        <span class="kpi-label">{{ item.nombre }}</span>
        <span class="kpi-value" :class="{ 'kpi-value--danger': item.nombre === 'Sin Sku' && item.valor > 0 }">{{ item.valor }}</span>
      </div>
    </div>

    <!-- SKU con diferentes stock -->
    <div class="section-card" v-if="modoLocal && productosDiferenteStock.length > 0">
      <div class="section-card__header">
        <h3 class="section-card__title">
          <i class="fa-solid fa-triangle-exclamation" style="color: #F59E0B"></i>
          SKU con diferente stock
        </h3>
        <span class="section-card__badge section-card__badge--warning">{{ productosDiferenteStock.length }}</span>
      </div>
      <div class="table-wrapper">
        <b-table
          :items="productosDiferenteStock"
          :fields="columnasStock"
          class="mb-0"
          hover
          small
          responsive
          thead-class="table-head"
          tbody-tr-class="table-row"
        >
          <template #cell(_img)="data">
            <img v-if="data.item.img" :src="data.item.img" class="cell-img" />
          </template>
          <template #cell(_id)="data">
            <a :href="data.item.productHref" target="_blank" class="cell-link">{{ data.item.id }}</a>
          </template>
          <template #cell(_idVariant)="data">
            <a :href="data.item.variantHref" target="_blank" class="cell-link">{{ data.item.idVariant }}</a>
          </template>
        </b-table>
      </div>
    </div>

    <!-- Productos sin SKU -->
    <div class="section-card">
      <div class="section-card__header">
        <h3 class="section-card__title">Productos sin SKU</h3>
        <div class="section-card__actions">
          <span class="section-card__badge section-card__badge--danger" v-if="productosSinSku.length > 0">{{ productosSinSku.length }}</span>
          <button class="btn-toggle-section" @click="mostrarSinSku = !mostrarSinSku">
            <i :class="mostrarSinSku ? 'fa-solid fa-chevron-up' : 'fa-solid fa-chevron-down'"></i>
          </button>
        </div>
      </div>
      <transition name="slide">
        <div v-if="mostrarSinSku && productosSinSku.length > 0">
          <div class="table-wrapper">
            <b-table
              :items="productosSinSku"
              :fields="columnasSku"
              class="mb-0"
              hover
              small
              responsive
              thead-class="table-head"
              tbody-tr-class="table-row"
            >
              <template #cell(_img)="data">
                <img v-if="data.item.img" :src="data.item.img" class="cell-img" />
                <div v-else class="cell-img-empty"><i class="fa-solid fa-image"></i></div>
              </template>
              <template #cell(product)="data">
                <span class="cell-text-bold">{{ data.item.product }}</span>
              </template>
              <template #cell(_id)="data">
                <a :href="data.item.productHref" target="_blank" class="cell-link">{{ data.item.id }}</a>
              </template>
              <template #cell(_idVariant)="data">
                <a :href="data.item.variantHref" target="_blank" class="cell-link">{{ data.item.idVariant }}</a>
              </template>
              <template #cell(_opt)="data">
                <button v-if="!data.item.sku" class="btn-action btn-action--green" @click="skuModal(data.item)">
                  <i class="fa-solid fa-plus"></i> Agregar SKU
                </button>
                <span v-else class="cell-sku">{{ data.item.sku }}</span>
              </template>
            </b-table>
          </div>
        </div>
      </transition>
      <div v-if="mostrarSinSku && productosSinSku.length === 0" class="empty-inline">
        <i class="fa-solid fa-circle-check"></i> Todos los productos tienen SKU
      </div>
    </div>

    <!-- SKU Listado -->
    <div class="section-card">
      <div class="section-card__header">
        <h3 class="section-card__title">SKU Listado</h3>
        <div class="section-card__actions">
          <div class="search-box">
            <i class="fa-solid fa-magnifying-glass search-box__icon"></i>
            <input type="text" class="search-box__input" placeholder="Buscar SKU..." v-model="buscarMatch" />
          </div>
          <button class="btn-toggle-section" @click="mostrarSku = !mostrarSku">
            <i :class="mostrarSku ? 'fa-solid fa-chevron-up' : 'fa-solid fa-chevron-down'"></i>
          </button>
        </div>
      </div>
      <transition name="slide">
        <div v-if="mostrarSku">
          <div class="table-wrapper">
            <b-table
              :items="variantesAllBusqueda"
              :fields="columnasVariants"
              class="mb-0"
              hover
              small
              responsive
              thead-class="table-head"
              tbody-tr-class="table-row"
            >
              <template #cell(_viewMore)="data">
                <button class="expand-btn" @click="data.toggleDetails">
                  <i :class="data.detailsShowing ? 'fa-solid fa-chevron-up' : 'fa-solid fa-chevron-down'"></i>
                </button>
              </template>
              <template #cell(_img)="data">
                <img v-if="data.item.img" :src="data.item.img" class="cell-img" />
                <div v-else class="cell-img-empty"><i class="fa-solid fa-image"></i></div>
              </template>
              <template #cell(sku)="data">
                <span class="cell-sku">{{ data.item.sku }}</span>
              </template>
              <template #cell(_productsLength)="data">
                <span class="cell-count-badge">{{ data.item.products.length }}</span>
              </template>
              <template #cell(_opt)="data">
                <button class="btn-action btn-action--blue" @click="skuModal(data.item)">
                  <i class="fa-solid fa-pen"></i> Cambiar
                </button>
              </template>

              <template #row-details="row">
                <div class="details-wrap">
                  <div class="details-inner">
                    <b-table
                      :items="row.item.products"
                      :fields="columnasProduct"
                      class="mb-0"
                      small
                      thead-class="details-head"
                      tbody-tr-class="details-row"
                    >
                      <template #cell(_img)="data">
                        <img v-if="data.item.img" :src="data.item.img" class="cell-img" />
                      </template>
                      <template #cell(_id)="data">
                        <a :href="data.item.productHref" target="_blank" class="cell-link">{{ data.item.id }}</a>
                      </template>
                      <template #cell(_idVariant)="data">
                        <a :href="data.item.variantHref" target="_blank" class="cell-link">{{ data.item.idVariant }}</a>
                      </template>
                    </b-table>
                  </div>
                </div>
              </template>
            </b-table>
          </div>
          <div v-infinite-scroll="loadMoreMatch" infinite-scroll-disabled="busy" infinite-scroll-distance="10"></div>
        </div>
      </transition>
    </div>
  </div>
</template>

<script>
import axios from '@axios'
import vSelect from 'vue-select'
import BCardCode from '@core/components/b-card-code'
import { BRow, BCol, BFormFile, BButton } from 'bootstrap-vue'
import { BTable } from 'bootstrap-vue'

export default {
  components: { BCardCode, BRow, BCol, BFormFile, BButton, vSelect, BTable },
  data(){
    return{
      columnasSku: [
        { key: "_img", label: "", thStyle: { width: "60px" } },
        { key: "product", label: "Producto", sortable: true },
        { key: "_id", label: "ID Product", sortable: true },
        { key: "_idVariant", label: "ID Variant", sortable: true },
        { key: "_opt", label: "", thStyle: { width: "140px" } },
      ],
      columnasVariants: [
        { key: "_viewMore", label: "", thStyle: { width: "40px" } },
        { key: "_img", label: "", thStyle: { width: "60px" } },
        { key: "sku", label: "SKU", sortable: true },
        { key: "_productsLength", label: "Productos", sortable: true, thStyle: { width: "100px" } },
        { key: "_opt", label: "", thStyle: { width: "130px" } },
      ],
      columnasStock: [
        { key: "_img", label: "", thStyle: { width: "60px" } },
        { key: "sku", label: "SKU", sortable: true },
        { key: "inventory_quantity", label: "Stock", sortable: true, thStyle: { width: "80px" } },
        { key: "_id", label: "ID Product", sortable: true },
        { key: "_idVariant", label: "ID Variant", sortable: true },
      ],
      columnasProduct: [
        { key: "product", label: "Producto", sortable: true },
        { key: "_id", label: "ID Product", sortable: true },
        { key: "_idVariant", label: "ID Variant", sortable: true },
      ],
      buscarMatch: '',
      countItemView: 50,
      nextScrollItemView: 40,
      defaultItemView: 50,
      productosAll: [],
      variantesAll: [],
      variantesAllBusqueda: [],
      productosSinSku: [],
      productosDiferenteStock: [],
      newSku: '',
      modoLocal: false,
      dataResumen: [
        { icon:'fa-solid fa-box', nombre: 'Productos', valor: 0, variant: 'primary' },
        { icon:'fa-solid fa-boxes-stacked', nombre: 'Variantes', valor: 0, variant: 'warning' },
        { icon:'fa-solid fa-terminal', nombre: 'Sku', valor: 0, variant: 'success' },
        { icon:'fa-solid fa-exclamation', nombre: 'Sin Sku', valor: 0, variant: 'danger' }
      ],
      mostrarSinSku: true,
      mostrarSku: true
    }
  },
  methods:{
    loadItems(){
      this.productosSinSku = [];
      this.productosDiferenteStock = [];
      this.variantesAll = [];

      Promise.all([axios.get("/shopify/products")])
        .then(([productsResponse]) => {
          this.productosAll = productsResponse.data;
          this.dataResumen[0].valor = this.productosAll.length;

          this.productosAll.forEach(product => {
            const variantsWithoutSku = product.variants.filter(variant => !variant.sku || variant.sku == "");
            variantsWithoutSku.forEach(variant => {
              const image = product.images.find(image => image.id === variant.image_id);
              const src = image ? image.src : '';
              this.productosSinSku.push({
                product: product.title, sku: variant.sku, img: src, id: product.id, idVariant: variant.id,
                productIdGraphql: product.admin_graphql_api_id, variantIdGraphql: variant.admin_graphql_api_id,
                productHref: 'https://admin.shopify.com/store/struendo/products/'+product.id,
                variantHref: 'https://admin.shopify.com/store/struendo/products/'+product.id+'/variants/'+variant.id,
              });
            });
          });

          this.dataResumen[3].valor = this.productosSinSku.length;
          let variantesAux = [];

          this.productosAll.forEach(product => {
            product.variants.forEach(variant => {
              const image = product.images.find(image => image.id === variant.image_id);
              const src = image ? image.src : '';
              variantesAux.push({
                product: product.title, sku: variant.sku, img: src, id: product.id, idVariant: variant.id,
                productIdGraphql: product.admin_graphql_api_id, variantIdGraphql: variant.admin_graphql_api_id,
                productHref: 'https://admin.shopify.com/store/struendo/products/'+product.id,
                variantHref: 'https://admin.shopify.com/store/struendo/products/'+product.id+'/variants/'+variant.id,
              });
            });
          });

          this.dataResumen[1].valor = variantesAux.length;

          variantesAux.forEach(variant => {
            const variantFind = this.variantesAll.find(f => f.sku === variant.sku);
            if(variantFind) { variantFind.products.push(variant); }
            else { variant.products = []; let aux = JSON.parse(JSON.stringify(variant)); variant.products.push(aux); this.variantesAll.push(variant); }
          });

          this.dataResumen[2].valor = this.variantesAll.length;
          this.variantesAllBusqueda = this.variantesAll.slice(0,this.defaultItemView);

          const skuMap = {};
          this.productosAll.forEach(producto => {
            producto.variants.forEach(variant => {
              const { sku } = variant;
              if (!skuMap[sku]) skuMap[sku] = [];
              variant.productHref = 'https://admin.shopify.com/store/struendo/products/'+producto.id;
              variant.variantHref = 'https://admin.shopify.com/store/struendo/products/'+producto.id+'/variants/'+variant.id;
              skuMap[sku].push(variant);
            });
          });

          Object.keys(skuMap).forEach(sku => {
            const stocks = skuMap[sku].map(variant => variant.inventory_quantity);
            const uniqueStocks = new Set(stocks);
            if (uniqueStocks.size > 1) this.productosDiferenteStock.push(...skuMap[sku]);
          });

          if(this.productosDiferenteStock.length > 0)
            this.modoLocal = window.location.href.includes('localhost');
        })
        .catch(error => { console.error("Error fetching data:", error); });
    },
    skuModal(data){
      this.$swal({ title: 'Escribe el nuevo SKU', input: 'text', inputPlaceholder: 'sku', showCloseButton: true }).then(result => {
        if (result.isConfirmed) {
          let auxSku = data.sku;
          data.sku = result.value;
          data.products.forEach(product => { product.sku = result.value; })

          if(data.products) {
            axios.post("/shopify/products/sku", { products: data.products }).then(r => r.data).then(res => {
              this.variantesAll.forEach(variant => {
                if(variant.sku == auxSku) { variant.sku = result.value; variant.products.forEach(product => { product.sku = result.value; }) }
              })
            })
          } else {
            axios.post("/shopify/product/sku", { product: data }).then(r => r.data).then(res => { this.dataResumen[3].valor = this.dataResumen[3].valor-1; })
          }
        }
      });
    },
    loadMoreMatch(){
      this.countItemView += this.nextScrollItemView;
      this.buscarMatchFuncion();
    },
    buscarMatchFuncion(items = this.variantesAll){
      let auxSorting = items;
      let aux = auxSorting.filter(item => {
        const tieneSku = !!item.sku;
        const coincideBusqueda = tieneSku && item.sku.toLowerCase().includes(this.buscarMatch.toLowerCase());
        const coincideNumProductos = item.products.length == this.buscarMatch;
        return coincideBusqueda || coincideNumProductos;
      });
      this.variantesAllBusqueda = aux.slice(0,this.countItemView);
    },
  },
  mounted() { this.loadItems(); },
  watch: {
    buscarMatch(){
      if(this.buscarMatch == "") this.countItemView = this.defaultItemView;
      this.buscarMatchFuncion();
    },
  }
}
</script>

<style lang="scss" scoped>
$primary: #3B82F6;
$success: #10B981;
$danger: #EF4444;
$warning: #F59E0B;
$border: #E2E8F0;
$text: #1E293B;
$text-muted: #64748B;
$bg: #F8FAFC;
$card-bg: #FFFFFF;
$radius: 12px;
$radius-sm: 8px;
$shadow: 0 1px 3px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04);

.skulist-page { max-width: 1200px; margin: 0 auto; padding: 0 8px; }

.sk-header { display: flex; justify-content: space-between; align-items: flex-end; margin-bottom: 20px; }
.header-eyebrow { display: block; font-family: 'JetBrains Mono', monospace; font-size: 11px; font-weight: 600; letter-spacing: 1.5px; text-transform: uppercase; color: $primary; margin-bottom: 2px; }
.header-title { font-family: 'Plus Jakarta Sans', sans-serif; font-size: 24px; font-weight: 700; color: $text; margin: 0; }

// KPI
.kpi-strip { display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px; margin-bottom: 20px; }
.kpi-card { background: $card-bg; border: 1px solid $border; border-radius: $radius-sm; padding: 16px 18px; display: flex; flex-direction: column; box-shadow: $shadow; }
.kpi-label { font-family: 'JetBrains Mono', monospace; font-size: 10px; font-weight: 600; letter-spacing: 1px; text-transform: uppercase; color: $text-muted; }
.kpi-value { font-family: 'JetBrains Mono', monospace; font-size: 22px; font-weight: 700; color: $text; font-feature-settings: "tnum"; margin: 4px 0 0; &--danger { color: $danger; } }

// Section card
.section-card {
  background: $card-bg; border: 1px solid $border; border-radius: $radius; box-shadow: $shadow; overflow: hidden; margin-bottom: 16px;

  &__header { display: flex; justify-content: space-between; align-items: center; padding: 16px 22px; border-bottom: 1px solid $border; }
  &__title { font-family: 'Plus Jakarta Sans', sans-serif; font-size: 15px; font-weight: 700; color: $text; margin: 0; display: flex; align-items: center; gap: 8px; }
  &__actions { display: flex; align-items: center; gap: 10px; }
  &__badge {
    display: inline-flex; align-items: center; justify-content: center; min-width: 26px; height: 22px; padding: 0 8px; border-radius: 12px;
    background: rgba($primary, 0.08); font-family: 'JetBrains Mono', monospace; font-size: 11px; font-weight: 700; color: $primary;
    &--warning { background: rgba($warning, 0.1); color: darken($warning, 10%); }
    &--danger { background: rgba($danger, 0.1); color: $danger; }
  }
}

.btn-toggle-section {
  width: 30px; height: 30px; border-radius: 6px; border: 1px solid $border; background: transparent; color: $text-muted;
  cursor: pointer; display: flex; align-items: center; justify-content: center; font-size: 11px; transition: all 0.15s ease;
  &:hover { border-color: $primary; color: $primary; }
}

.empty-inline {
  padding: 20px 22px; font-family: 'JetBrains Mono', monospace; font-size: 12px; color: $success;
  i { margin-right: 6px; }
}

// Search
.search-box {
  position: relative; width: 200px;
  &__icon { position: absolute; left: 10px; top: 50%; transform: translateY(-50%); color: $text-muted; font-size: 12px; }
  &__input {
    width: 100%; padding: 6px 10px 6px 30px; border: 1px solid $border; border-radius: $radius-sm;
    font-family: 'Plus Jakarta Sans', sans-serif; font-size: 13px; color: $text; background: transparent;
    &::placeholder { color: #CBD5E1; } &:focus { outline: none; border-color: $primary; }
  }
}

// Table
.table-wrapper { padding: 0; }

// Los ::v-deep van bajo .skulist-page a proposito: en la raiz, postcss-rtl + scoped generan [dir][data-v-xxx]
// y ninguna propiedad direccional (padding, margin...) llega a aplicarse.
.skulist-page ::v-deep .table-head th {
  font-family: 'JetBrains Mono', monospace; font-size: 10px; font-weight: 600; letter-spacing: 1px; text-transform: uppercase;
  color: $text-muted; border-top: none; border-bottom: 1px solid $border; padding: 8px 14px; background: transparent;
}
.skulist-page ::v-deep .table-row td { padding: 8px 14px; vertical-align: middle; border-color: lighten($border, 3%); font-size: 13px; }
.skulist-page ::v-deep .table-row:hover td { background: $bg; }

.cell-img { width: 70px; max-height: 50px; object-fit: contain; border-radius: 5px; }
.cell-img-empty { width: 70px; height: 50px; border-radius: 5px; border: 1px dashed $border; display: flex; align-items: center; justify-content: center; color: #CBD5E1; font-size: 13px; }
.cell-sku { font-family: 'JetBrains Mono', monospace; font-size: 12px; font-weight: 500; color: $text; }
.cell-text-bold { font-family: 'Plus Jakarta Sans', sans-serif; font-weight: 600; color: $text; }
.cell-link { font-family: 'JetBrains Mono', monospace; font-size: 11px; color: $primary; text-decoration: none; &:hover { text-decoration: underline; } }
.cell-count-badge {
  display: inline-flex; align-items: center; justify-content: center; min-width: 24px; height: 20px; padding: 0 6px; border-radius: 10px;
  background: rgba($primary, 0.08); font-family: 'JetBrains Mono', monospace; font-size: 11px; font-weight: 700; color: $primary;
}

// Action buttons
.btn-action {
  display: inline-flex; align-items: center; gap: 5px; padding: 5px 12px; border-radius: 6px; border: none;
  font-family: 'JetBrains Mono', monospace; font-size: 11px; font-weight: 600; cursor: pointer; transition: all 0.15s ease;

  &--green { background: rgba($success, 0.1); color: $success; &:hover { background: rgba($success, 0.18); } }
  &--blue { background: rgba($primary, 0.1); color: $primary; &:hover { background: rgba($primary, 0.18); } }
}

.expand-btn {
  width: 26px; height: 26px; border-radius: 6px; border: 1px solid $border; background: transparent; color: $text-muted;
  cursor: pointer; display: flex; align-items: center; justify-content: center; font-size: 10px; transition: all 0.15s ease;
  &:hover { border-color: $primary; color: $primary; }
}

// Details
.details-wrap { display: flex; justify-content: center; padding: 4px 22px 10px; }
.details-inner { width: 85%; background: $bg; border: 1px solid $border; border-radius: $radius-sm; padding: 8px 12px; }
.skulist-page ::v-deep .details-head th { font-family: 'JetBrains Mono', monospace; font-size: 9px; font-weight: 600; letter-spacing: 0.5px; text-transform: uppercase; color: $text-muted; border-bottom: 1px solid $border; border-top: none; padding: 6px 10px; }
.skulist-page ::v-deep .details-row td { border: none; padding: 5px 10px; font-size: 12px; color: $text; }

// Slide
.slide-enter-active, .slide-leave-active { transition: all 0.3s ease; max-height: 2000px; overflow: hidden; }
.slide-enter, .slide-leave-to { max-height: 0; opacity: 0; }

// Responsive
@media (max-width: 768px) { .kpi-strip { grid-template-columns: repeat(2, 1fr); } .search-box { width: 150px; } .details-inner { width: 100%; } }
</style>

<style lang="scss">
// Dark mode
.dark-layout {
  .skulist-page {
    .header-title { color: #E2E8F0; }
    .kpi-card { background: #1E293B; border-color: #334155; }
    .kpi-value { color: #F1F5F9; }

    .section-card { background: #1E293B; border-color: #334155; }
    .section-card__header { border-color: #334155; }
    .section-card__title { color: #E2E8F0; }

    .btn-toggle-section { border-color: #475569; color: #94A3B8; &:hover { border-color: #3B82F6; color: #3B82F6; } }

    .search-box__input { border-color: #334155; color: #E2E8F0; &::placeholder { color: #475569; } &:focus { border-color: #3B82F6; } }

    .table-head th { color: #94A3B8; border-color: #334155; }
    .table-row td { border-color: #334155; }
    .table-row:hover td { background: rgba(255,255,255,0.03); }
    .cell-sku { color: #E2E8F0; }
    .cell-text-bold { color: #E2E8F0; }
    .cell-img-empty { border-color: #334155; color: #475569; }
    .expand-btn { border-color: #475569; color: #94A3B8; }

    .details-inner { background: #0F172A; border-color: #334155; }
    .details-head th { color: #94A3B8; border-color: #334155; }
    .details-row td { color: #E2E8F0; }
  }
}
</style>
